import { adapter } from '@seek/tealium-adapter';

import { useGetProfileVisibility2 } from '../graphql/visibility/data';
import { useGetApproachability } from '../graphql/visibility/legacy-data';

import { allTrackingState, type InitialAppState } from './analytics';

const tealium = adapter();

type AnalyticsCurrentPageSection = 'employer site';

type AnalyticsProfileApproachabilityStatus = 'on' | 'off';

export interface ProfileTrackingState {
  profileVisibilityLevel?: string;
  profileApproachabilityStatus?: AnalyticsProfileApproachabilityStatus;
}

export let profileTrackingState: ProfileTrackingState;

export const setProfileTrackingState = (state: ProfileTrackingState): void => {
  profileTrackingState = state;
};

export const useSetProfileTrackingState = () => {
  const { profileVisibility2: visibilityLevel } = useGetProfileVisibility2();
  const { approachable } = useGetApproachability();

  const profileVisibilityLevel = visibilityLevel
    ? visibilityLevel.id.toLowerCase()
    : 'not set';

  const profileApproachabilityStatus = approachable ? 'on' : 'off';

  const profileState: ProfileTrackingState = {
    profileVisibilityLevel,
    profileApproachabilityStatus,
  };

  setProfileTrackingState(profileState);
};

export const createProfileAnalytics = (initialState: InitialAppState) => {
  const trackProfileCardOpened = ({
    currentPageSubsection,
  }: {
    currentPageSubsection: AnalyticsCurrentPageSection;
  }) => {
    tealium.link('profile_section_displayed', {
      ...initialState,
      ...allTrackingState(),
      eventName: 'profile_section_displayed',
      currentPage: 'visibility settings',
      currentPageSection: 'visibility settings',
      currentPageSectionState: 'edit',
      currentPageSubsection,
    });
  };

  const trackProfileVisibilityUpdated = (profileVisibilityLevel: string) => {
    tealium.link('profile_update_succeeded', {
      ...initialState,
      ...allTrackingState(),
      eventName: 'profile_update_succeeded',
      currentPage: 'visibility settings',
      currentPageSection: 'visibility settings',
      updateType: 'edit',
      currentPageSubsection: 'employer site',
      profileVisibilityLevel,
    });
  };

  const trackApproachabilityUpdated = ({
    approachable,
  }: {
    approachable: boolean;
  }) => {
    tealium.link('profile_update_succeeded', {
      ...initialState,
      ...allTrackingState(),
      eventName: 'profile_update_succeeded',
      currentPage: 'visibility settings',
      currentPageSection: 'visibility settings',
      updateType: 'edit',
      currentPageSubsection: 'approachability',
      profileApproachabilityStatus: approachable ? 'on' : 'off',
    });
  };

  return {
    trackProfileCardOpened,
    trackProfileVisibilityUpdated,
    trackApproachabilityUpdated,
  };
};

export type ProfileAnalytics = ReturnType<typeof createProfileAnalytics>;
